import AbstractService from '~/composables/analytics/services/AbstractService'
import type ProductImpressionData from '~/types/analytics/marketplace/ProductImpressionData'
import type ProductClickData from '~/types/analytics/marketplace/ProductClickData'
import type ProductViewData from '~/types/analytics/marketplace/ProductViewData'
import type ProductAddedToCartData from '~/types/analytics/marketplace/ProductAddedToCartData'
import type CartViewData from '~/types/analytics/marketplace/CartViewData'
import type BeginCheckoutData from '~/types/analytics/marketplace/BeginCheckoutData'
import type AddPaymentInfoData from '~/types/analytics/marketplace/AddPaymentInfoData'
import type PayButtonClickedData from '~/types/analytics/marketplace/PayButtonClickedData'
import type PaymentSuccessData from '~/types/analytics/checkout/PaymentSuccessData'
import type PaymentDeclinedData from '~/types/analytics/checkout/PaymentDeclinedData'
import type ShowMoreProductsData from '~/types/analytics/marketplace/search/ShowMoreProductsData'
import type ManualSearchData from '~/types/analytics/marketplace/search/ManualSearchData'
import type RefinedSearchData from '~/types/analytics/marketplace/search/RefinedSearchData'
import type QuotePaymentPageViewData from '~/types/analytics/quotes/checkout/QuotePaymentPageViewData'

import type QuotePaymentPageAddedDeliveryInfoData from '~/types/analytics/quotes/checkout/QuotePaymentPageAddedDeliveryInfoData'
import type QuotePaymentPageAddedPaymentInfoData from '~/types/analytics/quotes/checkout/QuotePaymentPageAddedPaymentInfoData'
import type QuotePaymentPagePayButtonClickData from '~/types/analytics/quotes/checkout/QuotePaymentPagePayButtonClickData'
enum EventName {
  QuSubmit = 'QuSubmit',
  PageViews = 'PageViews',
  QuotePaid = 'QuotePaid',
  MarketplaceViewItemList = 'view_item_list',
  MarketplaceSelectItem = 'select_item',
  MarketplaceViewProductDetails = 'view_item',
  MarketplaceAddToCart = 'add_to_cart',
  MarketpalceViewCart = 'view_cart',
  MarketplaceBeginCheckout = 'begin_checkout',
  MarketplaceAddPaymentInfo = 'add_payment_info',
  MarketplacePurchase = 'purchase',
  MarketplacePaymentSuccess = 'marketplace_payment_success',
  QuotePaymentSuccess = 'quote_payment_success',
  MarketplacePaymentDeclined = 'marketplace_payment_declined',
  QuotePaymentDeclined = 'quote_payment_declined',

  MarketplaceSearchBarShowMoreProducts = 'MarketplaceSearchBarShowMoreProducts',
  MarketplaceSearchBarManualSearch = 'MarketplaceSearchBarManualSearch',
  MarketplaceSearchBarRefinedSearch = 'MarketplaceSearchBarRefinedSearch',

  QuotePaymentPageView = 'QuotePaymentPageView',
  QuotePaymentPageAddedDeliveryInfo = 'QuotePaymentPageAddedDeliveryInfo',
  QuotePaymentPageAddedPaymentInfo = 'QuotePaymentPageAddedPaymentInfo',
  QuotePaymentPagePayButtonClick = 'QuotePaymentPagePayButtonClick',
}

function pushToDataLayer(data: Record<string, any>) {
  if (typeof window === 'undefined') {
    return
  }
  ;(window as any).dataLayer = (window as any).dataLayer || []
  ;(window as any).dataLayer.push(data)
}

export default class extends AbstractService {
  public trackQuoteSubmit(quoteId: number): void {
    pushToDataLayer({
      event: EventName.QuSubmit,
      type: 'QuoteRequestSuccess',
      id: quoteId.toString(),
    })
  }

  public trackPageLoad(): void {
    pushToDataLayer({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    })
  }

  public trackPageView(data: Record<string, any>): void {
    pushToDataLayer({
      event: EventName.PageViews,
      type: data.eventName,
    })
  }

  public trackQuotePaid(quoteId: number, valueInUSD: number): void {
    pushToDataLayer({
      event: EventName.QuotePaid,
      event_label: `qu${quoteId}`,
      value: valueInUSD,
    })
  }

  public trackMarketplaceProductImpressions(impressions: ProductImpressionData[]): void {
    pushToDataLayer({
      event: EventName.MarketplaceViewItemList,
      ecommerce: {
        items: impressions.map((predicate) => {
          return {
            item_id: predicate.id,
            item_name: predicate.name,
            item_brand: predicate.brand,
            item_category: predicate.category,
            index: predicate.index,
            price: predicate.price,
            currency: 'USD',
            item_list_id: 'marketplace_index',
            item_list_name: 'Marketplace Index',
          }
        }),
      },
    })
  }

  public trackMarketplaceProductClick(data: ProductClickData) {
    pushToDataLayer({
      event: EventName.MarketplaceSelectItem,
      interaction_place: {
        componentName: data.interaction_place.componentName,
      },
      ecommerce: {
        items: [
          {
            item_id: data.sku,
            item_name: data.name,
            item_brand: data.manufacturer,
            item_category: data.category,
            index: data.index,
            price: data.price,
            currency: 'USD',
          },
        ],
      },
    })
  }

  trackMarketplaceProductView(data: ProductViewData) {
    pushToDataLayer({
      event: EventName.MarketplaceViewProductDetails,
      ecommerce: {
        items: [
          {
            item_id: data.sku,
            item_name: data.name,
            item_brand: data.manufacturer,
            item_category: data.category,
            currency: 'USD',
            price: data.price,
            item_variant: data.variant,
          },
        ],
      },
    })
  }

  trackMarketplaceProductAddedToCart(data: ProductAddedToCartData) {
    pushToDataLayer({
      event: EventName.MarketplaceAddToCart,
      ecommerce: {
        items: [
          {
            item_name: data.name,
            item_id: data.sku,
            price: data.price,
            item_brand: data.manufacturer,
            item_category: data.category,
            item_variant: data.variant,
            quantity: data.quantity,
            currency: 'USD',
          },
        ],
      },
    })
  }

  trackViewCart(data: CartViewData) {
    pushToDataLayer({
      event: EventName.MarketpalceViewCart,
      ecommerce: {
        currency: 'USD',
        value: data.items
          .map((predicate) => predicate.price)
          .reduce((previousValue, currentValue) => previousValue + currentValue, 0.0),
        items: data.items.map((predicate, index) => {
          return {
            item_id: predicate.sku,
            item_name: predicate.name,
            currency: 'USD',
            index,
            item_brand: predicate.manufacturer,
            item_category: predicate.category,
            item_variant: predicate.variant,
            price: predicate.price,
            quantity: predicate.quantity,
          }
        }),
      },
    })
  }

  trackBeginCheckout(data: BeginCheckoutData) {
    pushToDataLayer({
      event: EventName.MarketplaceBeginCheckout,
      ecommerce: {
        items: data.items.map((predicate, index) => {
          return {
            item_id: predicate.sku,
            item_name: predicate.name,
            currency: 'USD',
            index,
            item_brand: predicate.manufacturer,
            item_category: predicate.category,
            item_variant: predicate.variant,
            price: predicate.price,
            quantity: predicate.quantity,
          }
        }),
      },
    })
  }

  trackMarketplaceAddPaymentInfo(data: AddPaymentInfoData) {
    pushToDataLayer({
      event: EventName.MarketplaceAddPaymentInfo,
      ecommerce: {
        currency: 'USD',
        value: data.value,
        payment_type: data.paymentType,
        items: data.items.map((predicate, index) => {
          return {
            item_id: predicate.sku,
            item_name: predicate.name,
            currency: 'USD',
            index,
            item_brand: predicate.manufacturer,
            item_category: predicate.category,
            item_variant: predicate.variant,
            price: predicate.price,
            quantity: predicate.quantity,
          }
        }),
      },
    })
  }

  trackMarketplacePayButtonClick(data: PayButtonClickedData) {
    pushToDataLayer({
      event: EventName.MarketplacePurchase,
      ecommerce: {
        transaction_id: `qu${data.quoteId}`,
        currency: 'USD',
        shipping: data.shipping,
        value: data.value,
        payment_type: data.paymentType,
        items: data.items.map((predicate, index) => {
          return {
            item_id: predicate.sku,
            item_name: predicate.name,
            currency: 'USD',
            index,
            item_brand: predicate.manufacturer,
            item_category: predicate.category,
            item_variant: predicate.variant,
            price: predicate.price,
            quantity: predicate.quantity,
          }
        }),
      },
    })
  }

  trackPaymentSuccessPage(data: PaymentSuccessData) {
    const eventName = data.marketplace ? EventName.MarketplacePaymentSuccess : EventName.QuotePaymentSuccess

    pushToDataLayer({
      event: eventName,
      ecommerce: {
        transaction_id: `qu${data.quoteId}`,
        currency: 'USD',
        value: data.value,
      },
    })
  }

  trackPaymentDeclinedPage(data: PaymentDeclinedData) {
    const eventName = data.marketplace ? EventName.MarketplacePaymentDeclined : EventName.QuotePaymentDeclined

    pushToDataLayer({
      event: eventName,
      ecommerce: {
        transaction_id: `qu${data.quoteId}`,
      },
    })
  }

  trackMarketplaceSearchBarShowMoreProducts(data: ShowMoreProductsData) {
    pushToDataLayer({
      event: EventName.MarketplaceSearchBarShowMoreProducts,
      searchText: data.searchText,
    })
  }

  trackMarketplaceSearchBarManualSearch(data: ManualSearchData) {
    pushToDataLayer({
      event: EventName.MarketplaceSearchBarManualSearch,
      searchText: data.searchText,
    })
  }

  trackMarketplaceSearchBarRefinedSearch(data: RefinedSearchData) {
    pushToDataLayer({
      event: EventName.MarketplaceSearchBarRefinedSearch,
      oldSearchText: data.oldSearchText,
      newSearchText: data.newSearchText,
      newSearchTextCategory: data.newSearchTextCategory,
    })
  }

  trackQuotePaymentPageView(data: QuotePaymentPageViewData) {
    pushToDataLayer({
      event: EventName.QuotePaymentPageView,
      quoteId: data.quoteId,
      value: data.value,
    })
  }

  trackQuotePaymentPageAddedDeliveryInfo(data: QuotePaymentPageAddedDeliveryInfoData) {
    pushToDataLayer({
      event: EventName.QuotePaymentPageAddedDeliveryInfo,
      quoteId: data.quoteId,
      value: data.value,
      shipping: data.shipping,
      deliveryFee: data.deliveryFee,
    })
  }

  trackQuotePaymentPageAddedPaymentInfo(data: QuotePaymentPageAddedPaymentInfoData) {
    pushToDataLayer({
      event: EventName.QuotePaymentPageAddedPaymentInfo,
      quoteId: data.quoteId,
      value: data.value,
      shipping: data.shipping,
      deliveryFee: data.deliveryFee,
      payment_type: data.paymentType,
    })
  }

  trackQuotePaymentPagePayButtonClick(data: QuotePaymentPagePayButtonClickData) {
    pushToDataLayer({
      event: EventName.QuotePaymentPagePayButtonClick,
      quoteId: data.quoteId,
      value: data.value,
      shipping: data.shipping,
      deliveryFee: data.deliveryFee,
      payment_type: data.paymentType,
    })
  }
}
